import { type Edge, type Node } from "@xyflow/react";

import { type AccessMapSuccessResponse, type NodeData } from "../types";
import {
  blueEdgeStyles,
  Configuration,
  GraphLeftNodes,
  grayEdgeStyles,
  NodesGroups,
} from "./configuration";
import { isEmpty } from "../../PoliciesForm/InventorySidePanel/util";

const parseNodeAndEdges = (
  id: string,
  data: AccessMapSuccessResponse,
  onMakeItFocusedNodeClick: (node: NodeData) => void,
  onNodeClick: (node: NodeData) => void,
) => {
  const nodes: NodeData[] = [];
  const edges: Edge[] = [];
  const graphConfig = {
    isLeftSectionExist: false,
    isRightSectionExist: false,
  };

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!data.data.nodes || data.data.nodes.length === 0) {
    return { nodes, edges };
  }

  // Find root node
  const rootNode = data.data.nodes.find((node) => node.id === id);

  if (!rootNode) {
    return { nodes, edges };
  }

  // Filter other nodes
  const nonRootNodes = data.data.nodes.filter((node) => node.id !== id);

  const filteredData = {} as Record<string, NodeData[]>;

  // Make grouping based on node type
  nonRootNodes.forEach((item) => {
    const { srn, label } = item;

    const relationshipType =
      data.data.edges.find((edge) => edge.source === srn || edge.target === srn)
        ?.relationshipType ?? "";

    const [, rType] = relationshipType.split("_");
    const labelRelationshipType = NodesGroups.includes(rType?.toLowerCase())
      ? rType
      : label;

    const type =
      labelRelationshipType.charAt(0)?.toUpperCase() +
      labelRelationshipType.slice(1)?.toLowerCase();

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!filteredData[type]) {
      filteredData[type] = [];
    }

    const usesRelationship = !isEmpty(
      data.data.edges.find(
        (edge) =>
          edge.target === item.srn && edge.relationshipType.startsWith("uses_"),
      ),
    );

    filteredData[type].push({
      ...(item as unknown as Node),
      usesRelationship,
      onMakeItFocusedNodeClick,
      onNodeClick,
      styles: {
        borderColor: usesRelationship ? blueEdgeStyles : grayEdgeStyles,
      },
    } as unknown as NodeData);
  });

  // Push nodes based on type and get their position and other config from configuration
  // eslint-disable-next-line no-restricted-syntax, guard-for-in
  for (const risk in filteredData) {
    const isLeftNode = GraphLeftNodes.includes(`${risk}s`);
    let source;
    let target;
    const usesRelationship = filteredData[risk].some(
      (filterData) => filterData.usesRelationship,
    );

    // Find group node Configuration
    const groupNodeConfig = Configuration[risk as keyof typeof Configuration];

    nodes.push({
      id: risk,
      ...groupNodeConfig,
      ariaLabel: risk,
      data: {
        label: `${risk}s`,
        riskCount: filteredData[risk].length,
      },
    } as unknown as NodeData);

    // Push the edge
    source = isLeftNode ? risk : rootNode.id;
    target = isLeftNode ? rootNode.id : risk;
    edges.push({
      id: `e_${source}_${target}`,
      source,
      target,
      style: usesRelationship ? blueEdgeStyles : grayEdgeStyles,
      type: "smoothstep",
    });

    // Find Scrollable Node Configuration
    const scrollableNodeConfig =
      Configuration[`${risk}ScrollableNode` as keyof typeof Configuration];

    // Push the node
    nodes.push({
      id: `${risk}ScrollableNode`,
      ...scrollableNodeConfig,
      ariaLabel: `${risk}ScrollableNode`,
      data: {
        isLeftNode,
        usesRelationship,
        type: `${risk}s`,
        list: filteredData[risk],
      },
    } as unknown as NodeData);

    // Push edge
    source = isLeftNode ? `${risk}ScrollableNode` : risk;
    target = isLeftNode ? risk : `${risk}ScrollableNode`;
    edges.push({
      id: `e_${source}_${target}`,
      source,
      target,
      style: usesRelationship ? blueEdgeStyles : grayEdgeStyles,
      type: "smoothstep",
    });

    if (!graphConfig.isLeftSectionExist && isLeftNode) {
      graphConfig.isLeftSectionExist = isLeftNode;
    }

    if (!graphConfig.isRightSectionExist && !isLeftNode) {
      graphConfig.isRightSectionExist = !isLeftNode;
    }
  }

  // Add Root Node
  nodes.push({
    ...Configuration.Root,
    ariaLabel: "root",
    data: {
      ...rootNode,
      ...graphConfig,
      isRoot: true,
      onMakeItFocusedNodeClick,
      onNodeClick,
    },
    id: rootNode.id,
  } as unknown as NodeData);

  return { nodes, edges };
};

export default parseNodeAndEdges;
